import getWindow from 'Utils/get-window';

import {
  deriveUsPrivacyString,
  GPP_SECTION_USNAT,
  UsnatFields,
} from './us-privacy-from-gpp';

/**
 * IAB consent readers shared by the ads stack (flint header bidding and
 * ads identity). The Sourcepoint CMP renders the __tcfapi/__uspapi/__gpp
 * stubs in the document head on every page (see
 * template/partials/external/sourcepoint.tsx); the stubs queue callbacks
 * until the CMP has loaded, so every read is wrapped in a short timeout to
 * guarantee the ad path is never stalled by a CMP that fails to load.
 */

// How long to wait on the CMP consent APIs before giving up (they answer
// synchronously once the CMP has loaded; the stubs queue callbacks until
// then).
export const CONSENT_TIMEOUT_MS = 300;

export type TcData = {
  tcString?: string;
  gdprApplies?: boolean;
  purpose?: {
    consents?: Record<string, boolean>;
  };
};
export type UspData = { uspString?: string };
export type GppData = { gppString?: string; applicableSections?: number[] };

type GppPingData = GppData & { signalStatus?: string };

type GppEvent = {
  eventName?: string;
  listenerId?: number;
  pingData?: GppPingData;
};

type ConsentWindow = {
  __tcfapi?: (
    command: string,
    version: number,
    callback: (data: TcData | null, success: boolean) => void,
  ) => void;
  __uspapi?: (
    command: string,
    version: number,
    callback: (data: UspData | null, success: boolean) => void,
  ) => void;
  __gpp?: (
    command: string,
    callback: (data: GppPingData | GppEvent | null, success: boolean) => void,
    parameter?: unknown,
  ) => void;
};

const getConsentWindow = (): ConsentWindow =>
  getWindow() as unknown as ConsentWindow;

// Resolve a CMP callback-style API with a timeout so a stub whose CMP never
// loads cannot stall the caller. The timer is cleared when the consent
// promise wins the race, so fast CMP responses don't leave timers queued.
const withConsentTimeout = <T>(
  promise: Promise<T | null>,
): Promise<T | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), CONSENT_TIMEOUT_MS);
    }),
  ]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  });
};

/**
 * Full IAB TCF consent data (GDPR), via the CMP's __tcfapi: the consent
 * string plus gdprApplies and per-purpose consents, for callers (ads
 * identity) that need more than the string itself. Resolves null when the
 * API is missing or the CMP has not answered within the timeout.
 */
export const getTcfConsentData = (): Promise<TcData | null> =>
  withConsentTimeout(
    new Promise<TcData | null>((resolve) => {
      const api = getConsentWindow().__tcfapi;
      if (typeof api !== 'function') {
        resolve(null);
        return;
      }
      try {
        api('getTCData', 2, (data, success) => {
          resolve((success && data) || null);
        });
      } catch (_) {
        resolve(null);
      }
    }),
  );

/** IAB TCF consent string (GDPR), via the CMP's __tcfapi. */
export const getTcfConsentString = (): Promise<string | null> =>
  getTcfConsentData().then((data) => data?.tcString || null);

const getUspApiString = (): Promise<string | null> =>
  withConsentTimeout(
    new Promise<string | null>((resolve) => {
      const api = getConsentWindow().__uspapi;
      if (typeof api !== 'function') {
        resolve(null);
        return;
      }
      try {
        api('getUSPData', 1, (data, success) => {
          resolve((success && data?.uspString) || null);
        });
      } catch (_) {
        resolve(null);
      }
    }),
  );

// getSection data is an array of subsection objects with the USNAT core
// first (GPP CMP API spec); some CMPs answer with the core object directly.
// Reading fields off the un-unwrapped array would default every signal to 0
// and fabricate "1---" over a real opt-out.
const unwrapUsnatSection = (data: unknown): UsnatFields | null => {
  const core = Array.isArray(data) ? data[0] : data;
  return core && typeof core === 'object' && !Array.isArray(core)
    ? (core as UsnatFields)
    : null;
};

/**
 * The USNAT section payload via a GPP CMP's getSection. The GPP spec does not
 * require the callback to run synchronously even after readiness, so this is
 * promise-shaped with the standard timeout. Exported with the api injected so
 * the synchronous, deferred, and array-shaped callback answers are all
 * unit-testable.
 */
export const getUsnatSectionViaApi = (
  api: NonNullable<ConsentWindow['__gpp']>,
): Promise<UsnatFields | null> =>
  withConsentTimeout(
    new Promise<UsnatFields | null>((resolve) => {
      try {
        api(
          'getSection',
          (data, success) => {
            resolve(success ? unwrapUsnatSection(data) : null);
          },
          'usnat',
        );
      } catch (_) {
        resolve(null);
      }
    }),
  );

/**
 * IAB US Privacy string (CCPA). When __uspapi is installed its answer is
 * authoritative, timeouts and empty answers included. Only when no __uspapi
 * exists at all does this derive the legacy string from the GPP USNAT section
 * (decision D1a; mapping and conformance vectors in us-privacy-from-gpp.ts).
 *
 * The absence check is the GOOGLE_CMP gate: Sourcepoint's inline stub installs
 * __uspapi on every page it serves, so with the flag off this behaves exactly
 * as before, and turning the flag off restores the stub and disables
 * derivation with it. Google Privacy & messaging serves no __uspapi. With JS
 * externals disabled neither API exists and the derivation resolves null.
 *
 * Null means no legacy signal exists: either no US regime applies, or the
 * user is on a per-state GPP section, which must not be mapped.
 */
export const getUspConsentString = (): Promise<string | null> => {
  if (typeof getConsentWindow().__uspapi === 'function') {
    return getUspApiString();
  }
  return getGppConsent().then((gpp) => {
    if (!gpp?.applicableSections?.includes(GPP_SECTION_USNAT)) {
      return null;
    }
    const gppApi = getConsentWindow().__gpp;
    if (typeof gppApi !== 'function') {
      return null;
    }
    return getUsnatSectionViaApi(gppApi).then((fields) =>
      deriveUsPrivacyString(gpp.applicableSections, fields),
    );
  });
};

/**
 * IAB GPP string + applicable section ids, via the CMP's __gpp.
 *
 * Uses `ping` rather than `getGPPData`: the latter was dropped in GPP 1.1,
 * which is the version our injected stub declares, so a conforming GPP-only
 * CMP need not answer it. `ping` is required in every version and its reply
 * carries both fields.
 *
 * Resolves null unless `signalStatus` is "ready". Before the CMP loads the
 * stub answers `ping` immediately with an empty `applicableSections`, and
 * treating that as "no regime applies" would be indistinguishable from a
 * genuine rest-of-world answer. Null means "not established"; callers are
 * expected to fail closed on it.
 *
 * A not-ready ping is not resolved immediately, because the stub always
 * answers that way and settling on it would spend none of the timeout
 * waiting for the CMP. Instead this registers a GPP event listener and
 * resolves on the first event carrying a ready `pingData`, letting
 * `withConsentTimeout` decide how long to wait. The listener is always
 * removed, on the ready path and on the timeout.
 */
export const getGppConsent = (): Promise<GppData | null> => {
  let cleanup = (): void => undefined;
  return withConsentTimeout(
    new Promise<GppData | null>((resolve) => {
      const api = getConsentWindow().__gpp;
      if (typeof api !== 'function') {
        resolve(null);
        return;
      }
      const toResult = (data: GppPingData): GppData => ({
        gppString: data.gppString,
        applicableSections: data.applicableSections,
      });
      try {
        api('ping', (data, success) => {
          const ping = (data || undefined) as GppPingData | undefined;
          if (success && ping?.signalStatus === 'ready') {
            resolve(toResult(ping));
            return;
          }
          // The stub always answers "not ready", so settling here would spend
          // none of the timeout waiting for the real CMP. Listen instead.
          let listenerId: number | undefined;
          cleanup = () => {
            if (listenerId === undefined) {
              return;
            }
            const id = listenerId;
            listenerId = undefined;
            try {
              api('removeEventListener', () => undefined, id);
            } catch (_) {
              // Inert either way once the promise has settled.
            }
          };
          api('addEventListener', (evt, ok) => {
            if (!ok || !evt) {
              resolve(null);
              return;
            }
            const event = evt as GppEvent;
            if (event.eventName === 'listenerRegistered') {
              listenerId = event.listenerId;
            }
            if (event.pingData?.signalStatus === 'ready') {
              resolve(toResult(event.pingData));
            }
          });
        });
      } catch (_) {
        resolve(null);
      }
    }),
  ).finally(() => {
    // Runs on the ready path and on the timeout, so the listener never leaks.
    cleanup();
  });
};
